import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { TimerRefHandle } from '~/components/SignatureVerification/DiiaQRCode.tsx';
import { selectIIT } from '~/store/iit/selectors.ts';
import { fetchGuaranteesApplication } from '~/store/guarantees/thunks/applicationThunks.ts';
import {
  type GuaranteesFileType,
  type TFilesSigningSaveSignedDocumentsResponse,
  type TGuaranteesApplicationFile,
} from '~/store/guarantees/types.ts';
import type { AppDispatch } from '~/store/store.ts';
import { useFilesSigning } from '../../shared/filesSigning/useFilesSigning.ts';
import {
  UDBTF_APPROVAL_LETTER_MISSING_SIGN_FILE_TEXT,
  UDBTF_APPROVAL_LETTER_MISSING_VERSION_TEXT,
  UDBTF_APPROVAL_LETTER_QR_TIMEOUT_TEXT,
} from './constants.ts';
import {
  buildApprovalLetterSigningDocuments,
  getApprovalLetterSigningFileId,
  getInternalApprovalLetterFile,
} from './utils.ts';

export const useInternalApprovalSigning = ({
  approvalLetterFile,
  approvalLetterFileType,
  conclusionAttachmentFile,
  conclusionAttachmentFileType,
  conclusionPreviewTitle,
  contragentId,
  dispatch,
  effectiveApplicationId,
  isConclusionAttachmentRequired,
  onApprove,
  previewTitle,
  refreshApplicationFiles,
  setModalAlert,
  showGuaranteesRequestError,
}: {
  approvalLetterFile?: TGuaranteesApplicationFile | null;
  approvalLetterFileType: GuaranteesFileType;
  conclusionAttachmentFile?: TGuaranteesApplicationFile | null;
  conclusionAttachmentFileType?: GuaranteesFileType;
  conclusionPreviewTitle?: string;
  contragentId: string;
  dispatch: AppDispatch;
  effectiveApplicationId: string;
  isConclusionAttachmentRequired?: boolean;
  onApprove: (versionOverride?: number) => Promise<boolean>;
  previewTitle: string;
  refreshApplicationFiles: () => Promise<TGuaranteesApplicationFile[]>;
  setModalAlert: (value: { text: string }) => void;
  showGuaranteesRequestError: (error: unknown) => void;
}) => {
  const { isLoading: isIITLoading } = useSelector(selectIIT) || {};
  const timerRef = useRef<TimerRefHandle>(null);
  const [activeApprovalLetterFile, setActiveApprovalLetterFile] =
    useState<TGuaranteesApplicationFile | null>(approvalLetterFile ?? null);
  const [activeConclusionAttachmentFile, setActiveConclusionAttachmentFile] =
    useState<TGuaranteesApplicationFile | null>(conclusionAttachmentFile ?? null);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [deepLink, setDeepLink] = useState('');
  const activeApprovalLetterFileId = getApprovalLetterSigningFileId(activeApprovalLetterFile);
  const activeConclusionAttachmentFileId = getApprovalLetterSigningFileId(
    activeConclusionAttachmentFile
  );
  const fileIds = useMemo(
    () =>
      [activeApprovalLetterFileId, activeConclusionAttachmentFileId].filter(
        (fileId): fileId is string => !!fileId
      ),
    [activeApprovalLetterFileId, activeConclusionAttachmentFileId]
  );
  const signingDocuments = useMemo(
    () =>
      buildApprovalLetterSigningDocuments([
        {
          file: activeApprovalLetterFile,
          fallbackName: previewTitle,
        },
        {
          file: activeConclusionAttachmentFile,
          fallbackName: conclusionPreviewTitle,
        },
      ]),
    [
      activeApprovalLetterFile,
      activeConclusionAttachmentFile,
      conclusionPreviewTitle,
      previewTitle,
    ]
  );
  const getActualApplicationVersion = async (
    saveResponse?: TFilesSigningSaveSignedDocumentsResponse
  ) => {
    if (typeof saveResponse?.version === 'number') {
      return saveResponse.version;
    }

    if (!contragentId || !effectiveApplicationId || effectiveApplicationId === 'new') {
      return null;
    }

    const refreshedApplication = await dispatch(
      fetchGuaranteesApplication({
        applicationId: effectiveApplicationId,
        contragentId,
      })
    ).unwrap();

    return typeof refreshedApplication?.version === 'number'
      ? refreshedApplication.version
      : null;
  };
  const onSigningSuccess = async (saveResponse?: TFilesSigningSaveSignedDocumentsResponse) => {
    setIsSignatureModalOpen(false);
    let version: number | null = null;

    try {
      version = await getActualApplicationVersion(saveResponse);
    } catch (error) {
      showGuaranteesRequestError(error);

      return;
    }

    if (typeof version !== 'number') {
      setModalAlert({ text: UDBTF_APPROVAL_LETTER_MISSING_VERSION_TEXT });

      return;
    }

    await onApprove(version);
  };
  const onSigningError = (message: string) => {
    setIsSignatureModalOpen(false);
    setModalAlert({ text: message });
  };
  const { handleIITSignature, onIITSign, handleDiiaSignature, resetSigningSession } =
    useFilesSigning({
      fileIds,
      applicationId: effectiveApplicationId,
      onSuccess: onSigningSuccess,
      onError: onSigningError,
      setDeepLink,
      timerRef,
    });

  useEffect(() => {
    setActiveApprovalLetterFile(approvalLetterFile ?? null);
  }, [approvalLetterFile]);

  useEffect(() => {
    setActiveConclusionAttachmentFile(conclusionAttachmentFile ?? null);
  }, [conclusionAttachmentFile]);

  const handleSignAndConfirm = useCallback(async () => {
    const approvalLetterFileId = getApprovalLetterSigningFileId(approvalLetterFile);
    const conclusionAttachmentFileId = getApprovalLetterSigningFileId(conclusionAttachmentFile);

    if (
      approvalLetterFileId &&
      (!isConclusionAttachmentRequired || conclusionAttachmentFileId)
    ) {
      setActiveApprovalLetterFile(approvalLetterFile ?? null);
      setActiveConclusionAttachmentFile(conclusionAttachmentFile ?? null);
      setIsSignatureModalOpen(true);

      return;
    }

    if (!effectiveApplicationId || effectiveApplicationId === 'new') {
      setModalAlert({ text: UDBTF_APPROVAL_LETTER_MISSING_SIGN_FILE_TEXT });

      return;
    }

    try {
      const refreshedApplicationFiles = await refreshApplicationFiles();
      const refreshedApprovalLetterFile = getInternalApprovalLetterFile(
        refreshedApplicationFiles,
        approvalLetterFileType
      );
      const refreshedConclusionAttachmentFile = conclusionAttachmentFileType
        ? getInternalApprovalLetterFile(refreshedApplicationFiles, conclusionAttachmentFileType)
        : null;
      const refreshedFileId = getApprovalLetterSigningFileId(refreshedApprovalLetterFile);
      const refreshedConclusionAttachmentFileId = getApprovalLetterSigningFileId(
        refreshedConclusionAttachmentFile
      );

      if (!refreshedFileId) {
        setModalAlert({ text: UDBTF_APPROVAL_LETTER_MISSING_SIGN_FILE_TEXT });

        return;
      }

      if (isConclusionAttachmentRequired && !refreshedConclusionAttachmentFileId) {
        setModalAlert({ text: UDBTF_APPROVAL_LETTER_MISSING_SIGN_FILE_TEXT });

        return;
      }

      setActiveApprovalLetterFile(refreshedApprovalLetterFile);
      setActiveConclusionAttachmentFile(refreshedConclusionAttachmentFile);
      setIsSignatureModalOpen(true);
    } catch (error) {
      showGuaranteesRequestError(error);
    }
  }, [
    approvalLetterFile,
    approvalLetterFileType,
    conclusionAttachmentFile,
    conclusionAttachmentFileType,
    effectiveApplicationId,
    isConclusionAttachmentRequired,
    refreshApplicationFiles,
    setModalAlert,
    showGuaranteesRequestError,
  ]);
  const handleCloseSignatureModal = () => {
    setIsSignatureModalOpen(false);
    resetSigningSession();
  };
  const handleTimeUp = () => {
    resetSigningSession();
    setIsSignatureModalOpen(false);
    showGuaranteesRequestError({ message: UDBTF_APPROVAL_LETTER_QR_TIMEOUT_TEXT });
  };

  return {
    deepLink,
    handleCloseSignatureModal,
    handleDiiaSignature,
    handleIITSignature,
    handleSignAndConfirm,
    handleTimeUp,
    isIITLoading,
    isSignatureModalOpen,
    onIITSign,
    signingDocuments,
    timerRef,
  };
};
